import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_DYNAMIC_PROP,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_STATIC,
} from '../core/PhysicsWorld';
import { TrafficSigns } from './TrafficSigns';

export interface RoadSegment {
  id: string;
  start: THREE.Vector3;
  end: THREE.Vector3;
  width: number;
  speedLimitKmh: number;
  hasSolidLine: boolean; // Zeichen 295
  isPriorityRoad: boolean; // Vorfahrtsstraße
  isOneWay?: boolean;
  isBicycleStreet?: boolean;
  isMotorway?: boolean;
}

export class RoadNetwork {
  public group: THREE.Group;
  public sperrGroup: THREE.Group;
  public segments: RoadSegment[] = [];

  private roadMaterial: THREE.MeshStandardMaterial;
  private lineWhiteMat: THREE.MeshBasicMaterial;
  private sidewalkMat: THREE.MeshStandardMaterial;
  private curbMat: THREE.MeshStandardMaterial;

  constructor(private physicsWorld?: PhysicsWorld) {
    this.group = new THREE.Group();
    this.sperrGroup = new THREE.Group();
    this.sperrGroup.name = 'sperrflaechen';
    this.group.add(this.sperrGroup);

    this.roadMaterial = new THREE.MeshStandardMaterial({
      color: 0x161b22, // Crisp dark asphalt
      roughness: 0.86,
      metalness: 0.08,
    });
    this.lineWhiteMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.curbMat = new THREE.MeshStandardMaterial({
      color: 0x94a3b8, // Light concrete / stone curb
      roughness: 0.85,
      metalness: 0.1,
    });
    this.sidewalkMat = new THREE.MeshStandardMaterial({ color: 0x64748b, roughness: 0.9 });

    this.buildGridRoads();
  }

  private buildGridRoads() {
    // 1. Main Arterial Boulevard (Tempo 50, Vorfahrtsstraße) running along Z axis (x = 0, width 8.0m, 2 rows/lanes)
    // North branch (z > 0):
    // Segment 1: from Roundabout (z = 18) to Signalized Intersection South edge (z = 56).
    // Center line: solid (Zeichen 295) near pedestrian refuge and roundabout, ending before the junction.
    this.createStraightRoad('main_n_1', new THREE.Vector3(0, 0, 18), new THREE.Vector3(0, 0, 56), 8.0, 50, 'solid', true);
    // Segment 2: from Signalized Intersection North edge (z = 64) to Ring North (z = 116).
    // Center line: dashed Leitlinie (Zeichen 340).
    this.createStraightRoad('main_n_2', new THREE.Vector3(0, 0, 64), new THREE.Vector3(0, 0, 116), 8.0, 50, 'dashed', true);

    // South branch (z < 0):
    this.createStraightRoad('main_s_1', new THREE.Vector3(0, 0, -18), new THREE.Vector3(0, 0, -56), 8.0, 50, 'solid', true);
    this.createStraightRoad('main_s_2', new THREE.Vector3(0, 0, -64), new THREE.Vector3(0, 0, -116), 8.0, 50, 'dashed', true);

    // 2. Main Avenue East-West (Tempo 50, Vorfahrtsstraße) running along X axis (z = 0, width 8.0m, 2 rows/lanes)
    this.createStraightRoad('main_e_1', new THREE.Vector3(18, 0, 0), new THREE.Vector3(56, 0, 0), 8.0, 50, 'dashed', true);
    this.createStraightRoad('main_e_2', new THREE.Vector3(64, 0, 0), new THREE.Vector3(116, 0, 0), 8.0, 50, 'dashed', true);
    this.createStraightRoad('main_w_1', new THREE.Vector3(-18, 0, 0), new THREE.Vector3(-56, 0, 0), 8.0, 50, 'dashed', true);
    this.createStraightRoad('main_w_2', new THREE.Vector3(-64, 0, 0), new THREE.Vector3(-116, 0, 0), 8.0, 50, 'dashed', true);

    // 3. Outer Ring Road (Stadtring / Perimeter Boulevard - Tempo 50, width 8.0m, 2 rows/lanes):
    // Segmented cleanly around intersections so curbs and sidewalks do not obstruct T-junction openings:
    // North Ring (z = 120):
    this.createStraightRoad('ring_n_w', new THREE.Vector3(-116, 0, 120), new THREE.Vector3(-53.5, 0, 120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_n_mw', new THREE.Vector3(-46.5, 0, 120), new THREE.Vector3(-4.0, 0, 120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_n_me', new THREE.Vector3(4.0, 0, 120), new THREE.Vector3(46.5, 0, 120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_n_e', new THREE.Vector3(53.5, 0, 120), new THREE.Vector3(116, 0, 120), 8.0, 50, 'dashed', true);

    // South Ring (z = -120):
    this.createStraightRoad('ring_s_w', new THREE.Vector3(-116, 0, -120), new THREE.Vector3(-53.5, 0, -120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_s_mw', new THREE.Vector3(-46.5, 0, -120), new THREE.Vector3(-4.0, 0, -120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_s_me', new THREE.Vector3(4.0, 0, -120), new THREE.Vector3(46.5, 0, -120), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_s_e', new THREE.Vector3(53.5, 0, -120), new THREE.Vector3(116, 0, -120), 8.0, 50, 'dashed', true);

    // East Ring (x = 120):
    this.createStraightRoad('ring_e_s', new THREE.Vector3(120, 0, -116), new THREE.Vector3(120, 0, -63.5), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_e_ms', new THREE.Vector3(120, 0, -56.5), new THREE.Vector3(120, 0, -4.0), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_e_mn', new THREE.Vector3(120, 0, 4.0), new THREE.Vector3(120, 0, 56.5), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_e_n', new THREE.Vector3(120, 0, 63.5), new THREE.Vector3(120, 0, 116), 8.0, 50, 'dashed', true);

    // West Ring (x = -120):
    this.createStraightRoad('ring_w_s', new THREE.Vector3(-120, 0, -116), new THREE.Vector3(-120, 0, -63.5), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_w_ms', new THREE.Vector3(-120, 0, -56.5), new THREE.Vector3(-120, 0, -4.0), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_w_mn', new THREE.Vector3(-120, 0, 4.0), new THREE.Vector3(-120, 0, 56.5), 8.0, 50, 'dashed', true);
    this.createStraightRoad('ring_w_n', new THREE.Vector3(-120, 0, 63.5), new THREE.Vector3(-120, 0, 116), 8.0, 50, 'dashed', true);

    // 4. Residential 30-Zone Streets (Tempo 30, Rechts-vor-Links, width 6.5m, 2 shared rows/undivided carriageway):
    // Per VwV-StVO zu § 45 Abs. 1c: NO center lines and NO edge lines in 30-zones!
    // North residential street (z = 60):
    this.createStraightRoad('res_30_n1_w', new THREE.Vector3(-116, 0, 60), new THREE.Vector3(-53.5, 0, 60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_n1_mw', new THREE.Vector3(-46.5, 0, 60), new THREE.Vector3(-4.0, 0, 60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_n1_me', new THREE.Vector3(4.0, 0, 60), new THREE.Vector3(46.5, 0, 60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_n1_e', new THREE.Vector3(53.5, 0, 60), new THREE.Vector3(116, 0, 60), 6.5, 30, 'none', false, false);

    // South residential street (z = -60):
    this.createStraightRoad('res_30_s1_w', new THREE.Vector3(-116, 0, -60), new THREE.Vector3(-53.5, 0, -60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_s1_mw', new THREE.Vector3(-46.5, 0, -60), new THREE.Vector3(-4.0, 0, -60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_s1_me', new THREE.Vector3(4.0, 0, -60), new THREE.Vector3(46.5, 0, -60), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_s1_e', new THREE.Vector3(53.5, 0, -60), new THREE.Vector3(116, 0, -60), 6.5, 30, 'none', false, false);

    // East residential cross street (x = 50):
    this.createStraightRoad('res_30_cross_e_s', new THREE.Vector3(50, 0, -116), new THREE.Vector3(50, 0, -63.5), 6.5, 30, 'none', false, false, false, true, false);
    this.createStraightRoad('res_30_cross_e_ms', new THREE.Vector3(50, 0, -56.5), new THREE.Vector3(50, 0, -4.0), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_e_mn_s', new THREE.Vector3(50, 0, 4.0), new THREE.Vector3(50, 0, 26.75), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_e_mn_n', new THREE.Vector3(50, 0, 33.25), new THREE.Vector3(50, 0, 56.5), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_e_n', new THREE.Vector3(50, 0, 63.5), new THREE.Vector3(50, 0, 116), 6.5, 30, 'none', false, false);

    // West residential cross street (x = -50):
    this.createStraightRoad('res_30_cross_w_s', new THREE.Vector3(-50, 0, -116), new THREE.Vector3(-50, 0, -63.5), 6.5, 30, 'none', false, false, false, true, false);
    this.createStraightRoad('res_30_cross_w_ms', new THREE.Vector3(-50, 0, -56.5), new THREE.Vector3(-50, 0, -4.0), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_w_mn_s', new THREE.Vector3(-50, 0, 4.0), new THREE.Vector3(-50, 0, 26.75), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_w_mn_n', new THREE.Vector3(-50, 0, 33.25), new THREE.Vector3(-50, 0, 56.5), 6.5, 30, 'none', false, false);
    this.createStraightRoad('res_30_cross_w_n', new THREE.Vector3(-50, 0, 63.5), new THREE.Vector3(-50, 0, 116), 6.5, 30, 'none', false, false);

    // 5. Intersections
    // A. Outer Ring Corners (NW, NE, SW, SE)
    this.buildIntersection(new THREE.Vector3(-120, 0, 120), 8.0, false);
    this.buildIntersection(new THREE.Vector3(120, 0, 120), 8.0, false);
    this.buildIntersection(new THREE.Vector3(-120, 0, -120), 8.0, false);
    this.buildIntersection(new THREE.Vector3(120, 0, -120), 8.0, false);

    // B. Outer Ring T-Junctions
    this.buildIntersection(new THREE.Vector3(0, 0, 120), 8.0, false);
    this.buildIntersection(new THREE.Vector3(0, 0, -120), 8.0, false);
    this.buildIntersection(new THREE.Vector3(120, 0, 0), 8.0, false);
    this.buildIntersection(new THREE.Vector3(-120, 0, 0), 8.0, false);
    this.buildIntersection(new THREE.Vector3(50, 0, 120), 7.0, false);
    this.buildIntersection(new THREE.Vector3(-50, 0, 120), 7.0, false);
    this.buildIntersection(new THREE.Vector3(50, 0, -120), 7.0, false);
    this.buildIntersection(new THREE.Vector3(-50, 0, -120), 7.0, false);
    this.buildIntersection(new THREE.Vector3(120, 0, 60), 7.0, false);
    this.buildIntersection(new THREE.Vector3(-120, 0, 60), 7.0, false);
    this.buildIntersection(new THREE.Vector3(120, 0, -60), 7.0, false);
    this.buildIntersection(new THREE.Vector3(-120, 0, -60), 7.0, false);

    // C. Inner Grid Intersections
    this.buildIntersection(new THREE.Vector3(50, 0, 60), 6.5, false);  // Rechts-vor-Links (unmarked)
    this.buildIntersection(new THREE.Vector3(-50, 0, 60), 6.5, false); // Rechts-vor-Links (unmarked)
    this.buildIntersection(new THREE.Vector3(50, 0, 30), 6.5, false);  // Fahrradstraße / Querstraße Ost
    this.buildIntersection(new THREE.Vector3(-50, 0, 30), 6.5, false); // Fahrradstraße / Querstraße West
    this.buildIntersection(new THREE.Vector3(50, 0, -60), 6.5, false); // Rechts-vor-Links (unmarked)
    this.buildIntersection(new THREE.Vector3(-50, 0, -60), 6.5, false);// Rechts-vor-Links (unmarked)
    this.buildIntersection(new THREE.Vector3(0, 0, 60), 8.0, true);    // Traffic Light intersection North
    this.buildIntersection(new THREE.Vector3(0, 0, -60), 8.0, true);   // Traffic Light intersection South
    this.buildIntersection(new THREE.Vector3(60, 0, 0), 8.0, true);    // Traffic Light intersection East
    this.buildIntersection(new THREE.Vector3(-60, 0, 0), 8.0, true);   // Traffic Light intersection West

    // 6. StVO Road Markings: Zebrastreifen (Zeichen 293)
    this.createZebraCrossing(new THREE.Vector3(0, 0, 42), 8.0, 0);       // Boulevard crossing (clear approach, NO haltelinie)
    this.createZebraCrossing(new THREE.Vector3(0, 0, 78), 8.0, 0);       // North of traffic lights
    this.createZebraCrossing(new THREE.Vector3(30, 0, 60), 6.5, Math.PI / 2); // Residential crossing
    this.createZebraCrossing(new THREE.Vector3(-30, 0, 60), 6.5, Math.PI / 2);

    // 7. StVO Road Markings: Directional Lane Arrows (Zeichen 297)
    // Single-lane approaches feature combined arrows (Geradeaus und Abbiegen) so left turns into 30-zone are not blocked:
    // Northbound approach to (0, 0, 60): right lane is x = -2.0, pointing North (rotY = 0)
    this.createLaneArrow('straight-left', new THREE.Vector3(-2.0, 0, 48), 0);
    // Southbound approach to (0, 0, 60): right lane is x = +2.0, pointing South (rotY = Math.PI)
    this.createLaneArrow('straight-right', new THREE.Vector3(2.0, 0, 72), Math.PI);
    // Northbound approach to (0, 0, -60):
    this.createLaneArrow('straight-right', new THREE.Vector3(-2.0, 0, -72), 0);
    // Southbound approach to (0, 0, -60):
    this.createLaneArrow('straight-left', new THREE.Vector3(2.0, 0, -48), Math.PI);

    // 8. StVO Road Markings: Asphalt Speed Limit Ovals (Tempo 30 / Tempo 50)
    this.createSpeedLimitMarking(50, new THREE.Vector3(-2.0, 0, 24), 0);         // Northbound leaving roundabout
    this.createSpeedLimitMarking(50, new THREE.Vector3(2.0, 0, 100), Math.PI);   // Southbound entering from Ring
    this.createSpeedLimitMarking(30, new THREE.Vector3(12.0, 0, 58.5), Math.PI / 2); // Entering Goethestr. 30-zone
    this.createSpeedLimitMarking(30, new THREE.Vector3(-12.0, 0, 61.5), -Math.PI / 2); // Entering Bahnhofstr. 30-zone
    this.createSpeedLimitMarking(30, new THREE.Vector3(50, 0, 20), 0);

    // 9. StVO Road Markings: Sperrfläche (Zeichen 298)
    // In German traffic design (RAL / StVO), raised concrete is strictly restricted to middle-of-road medians.
    // At T-junctions and road entries, flat Sperrflächen (Zeichen 298) are painted directly on the asphalt:

    // A. Boulevard center refuge approach (Mittelinsel / Splitter vor Kreisverkehr):
    this.createSperrflaeche(new THREE.Vector3(0, 0, 28), 1.6, 6.0, 0);

    // B. North Ring T-Junction (0, 0, 120):
    // Buffer Sperrfläche opposite Boulevard stem along guardrail perimeter:
    this.createSperrflaeche(new THREE.Vector3(0, 0, 123.2), 7.6, 1.4, Math.PI / 2);
    // Channelizing corner Sperrflächen guiding right and left turns without curb obstruction:
    this.createCornerSperrflaeche(new THREE.Vector3(4.6, 0, 117.2), 2.2, 3.2, Math.PI / 4, 'right');
    this.createCornerSperrflaeche(new THREE.Vector3(-4.6, 0, 117.2), 2.2, 3.2, -Math.PI / 4, 'left');

    // C. South Ring T-Junction (0, 0, -120):
    this.createSperrflaeche(new THREE.Vector3(0, 0, -123.2), 7.6, 1.4, Math.PI / 2);
    this.createCornerSperrflaeche(new THREE.Vector3(4.6, 0, -117.2), 2.2, 3.2, (3 * Math.PI) / 4, 'left');
    this.createCornerSperrflaeche(new THREE.Vector3(-4.6, 0, -117.2), 2.2, 3.2, -(3 * Math.PI) / 4, 'right');

    // D. East Ring T-Junction (120, 0, 0):
    this.createSperrflaeche(new THREE.Vector3(123.2, 0, 0), 1.4, 7.6, 0);
    this.createCornerSperrflaeche(new THREE.Vector3(117.2, 0, 4.6), 2.2, 3.2, Math.PI / 4, 'left');
    this.createCornerSperrflaeche(new THREE.Vector3(117.2, 0, -4.6), 2.2, 3.2, (3 * Math.PI) / 4, 'right');

    // E. West Ring T-Junction (-120, 0, 0):
    this.createSperrflaeche(new THREE.Vector3(-123.2, 0, 0), 1.4, 7.6, 0);
    this.createCornerSperrflaeche(new THREE.Vector3(-117.2, 0, 4.6), 2.2, 3.2, -Math.PI / 4, 'right');
    this.createCornerSperrflaeche(new THREE.Vector3(-117.2, 0, -4.6), 2.2, 3.2, -(3 * Math.PI) / 4, 'left');

    // F. Outer Ring Cross Street Entries (x = ±50):
    this.createSperrflaeche(new THREE.Vector3(50, 0, 123.2), 6.2, 1.4, Math.PI / 2);
    this.createSperrflaeche(new THREE.Vector3(-50, 0, 123.2), 6.2, 1.4, Math.PI / 2);
    this.createSperrflaeche(new THREE.Vector3(50, 0, -123.2), 6.2, 1.4, Math.PI / 2);
    this.createSperrflaeche(new THREE.Vector3(-50, 0, -123.2), 6.2, 1.4, Math.PI / 2);

    // 10. German StVO Bus Stop Markings: Zick-Zack-Linie (Zeichen 299) & "BUS" Stencil
    // Along Southbound curb at x = 3.6, z = 15 (near Haltestellenschild Z224)
    this.createZickZackLinie(new THREE.Vector3(3.4, 0, 15), 14.0, 0);
    this.createAsphaltStencil('BUS', new THREE.Vector3(2.0, 0, 15), Math.PI, 1.8, 3.2);

    // 11. StVO Railway Crossing (Bahnübergang mit Andreaskreuz Zeichen 201 & Baken Zeichen 156/159/162)
    this.createRailwayCrossing(new THREE.Vector3(70, 0, 120), 8.0);

    // 12. StVO Road Markings: Turn Navigation Guidance Lines (Zeichen 340 Fahrstreifenführung für Linksabbieger)
    this.createTurnTrajectoryArrows(new THREE.Vector3(0, 0, 60), 'both', 0);
    this.createTurnTrajectoryArrows(new THREE.Vector3(0, 0, -60), 'both', 0);
    this.createTurnTrajectoryArrows(new THREE.Vector3(60, 0, 0), 'both', Math.PI / 2);
    this.createTurnTrajectoryArrows(new THREE.Vector3(-60, 0, 0), 'both', -Math.PI / 2);

    this.buildExpandedRoads();
  }

  private buildExpandedRoads() {
    // 1. Multi-Lane Urban Boulevards (4-lane, 15.0m wide, Tempo 50) starting beyond ring perimeter (128m)
    this.createStraightRoad('ml_n', new THREE.Vector3(0, 0, 128), new THREE.Vector3(0, 0, 200), 15.0, 50, 'solid', true, true, false, false, false);
    this.createStraightRoad('ml_s', new THREE.Vector3(0, 0, -128), new THREE.Vector3(0, 0, -200), 15.0, 50, 'solid', true, true, false, false, false);
    this.createStraightRoad('ml_e', new THREE.Vector3(128, 0, 0), new THREE.Vector3(200, 0, 0), 15.0, 50, 'solid', true, true, false, false, false);
    this.createStraightRoad('ml_w', new THREE.Vector3(-128, 0, 0), new THREE.Vector3(-200, 0, 0), 15.0, 50, 'solid', true, true, false, false, false);

    // 2. Landstraße (8.0m, Tempo 100) — connecting ring/boulevard to Autobahn interchanges
    this.createStraightRoad('land_n', new THREE.Vector3(0, 0, 200), new THREE.Vector3(0, 0, 520), 8.0, 100, 'dashed', true, true, false, false, false);
    this.createStraightRoad('land_s', new THREE.Vector3(0, 0, -200), new THREE.Vector3(0, 0, -520), 8.0, 100, 'dashed', true, true, false, false, false);
    this.createStraightRoad('land_e', new THREE.Vector3(200, 0, 0), new THREE.Vector3(520, 0, 0), 8.0, 100, 'dashed', true, true, false, false, false);
    this.createStraightRoad('land_w', new THREE.Vector3(-200, 0, 0), new THREE.Vector3(-520, 0, 0), 8.0, 100, 'dashed', true, true, false, false, false);

    // 3. Autobahn Loop (dual carriageway, 7.5m width per direction, Tempo 130)
    // North side (z = -580):
    this.createStraightRoad('ab_n_east', new THREE.Vector3(-560, 0, -574.75), new THREE.Vector3(560, 0, -574.75), 7.5, 130, 'dashed', true, true, true, false, false);
    this.createStraightRoad('ab_n_west', new THREE.Vector3(560, 0, -585.25), new THREE.Vector3(-560, 0, -585.25), 7.5, 130, 'dashed', true, true, true, false, false);

    // South side (z = 580):
    this.createStraightRoad('ab_s_west', new THREE.Vector3(-560, 0, 574.75), new THREE.Vector3(560, 0, 574.75), 7.5, 130, 'dashed', true, true, true, false, false);
    this.createStraightRoad('ab_s_east', new THREE.Vector3(560, 0, 585.25), new THREE.Vector3(-560, 0, 585.25), 7.5, 130, 'dashed', true, true, true, false, false);

    // East side (x = 580):
    this.createStraightRoad('ab_e_south', new THREE.Vector3(574.75, 0, -560), new THREE.Vector3(574.75, 0, 560), 7.5, 130, 'dashed', true, true, true, false, false);
    this.createStraightRoad('ab_e_north', new THREE.Vector3(585.25, 0, 560), new THREE.Vector3(585.25, 0, -560), 7.5, 130, 'dashed', true, true, true, false, false);

    // West side (x = -580):
    this.createStraightRoad('ab_w_north', new THREE.Vector3(-574.75, 0, 560), new THREE.Vector3(-574.75, 0, -560), 7.5, 130, 'dashed', true, true, true, false, false);
    this.createStraightRoad('ab_w_south', new THREE.Vector3(-585.25, 0, -560), new THREE.Vector3(-585.25, 0, 560), 7.5, 130, 'dashed', true, true, true, false, false);

    // Corner bridges (NW, NE, SW, SE)
    this.buildIntersection(new THREE.Vector3(-580, 0, -580), 25.0, false);
    this.buildIntersection(new THREE.Vector3(580, 0, -580), 25.0, false);
    this.buildIntersection(new THREE.Vector3(-580, 0, 580), 25.0, false);
    this.buildIntersection(new THREE.Vector3(580, 0, 580), 25.0, false);

    // 4. On-Ramp and Off-Ramp connectors (N, S, E, W interchanges)
    // North interchange:
    this.createStraightRoad('ramp_on_n', new THREE.Vector3(4, 0, -520), new THREE.Vector3(4, 0, -574), 4.0, 100, 'none', false, true, false, false, false);
    this.createStraightRoad('ramp_off_n', new THREE.Vector3(-4, 0, -574), new THREE.Vector3(-4, 0, -520), 4.0, 100, 'none', false, true, false, false, false);

    // South interchange:
    this.createStraightRoad('ramp_on_s', new THREE.Vector3(-4, 0, 520), new THREE.Vector3(-4, 0, 574), 4.0, 100, 'none', false, true, false, false, false);
    this.createStraightRoad('ramp_off_s', new THREE.Vector3(4, 0, 574), new THREE.Vector3(4, 0, 520), 4.0, 100, 'none', false, true, false, false, false);

    // East interchange:
    this.createStraightRoad('ramp_on_e', new THREE.Vector3(520, 0, 4), new THREE.Vector3(574, 0, 4), 4.0, 100, 'none', false, true, false, false, false);
    this.createStraightRoad('ramp_off_e', new THREE.Vector3(574, 0, -4), new THREE.Vector3(520, 0, -4), 4.0, 100, 'none', false, true, false, false, false);

    // West interchange:
    this.createStraightRoad('ramp_on_w', new THREE.Vector3(-520, 0, -4), new THREE.Vector3(-574, 0, -4), 4.0, 100, 'none', false, true, false, false, false);
    this.createStraightRoad('ramp_off_w', new THREE.Vector3(-574, 0, 4), new THREE.Vector3(-520, 0, 4), 4.0, 100, 'none', false, true, false, false, false);

    // 5. One-Way Signs (Z220) for the residential cross streets
    const signOneway1 = TrafficSigns.createSign('Z220_EINBAHNSTRASSE', new THREE.Vector3(53.5, 0, -112), 0);
    const signOneway2 = TrafficSigns.createSign('Z220_EINBAHNSTRASSE', new THREE.Vector3(-46.5, 0, -112), 0);
    this.group.add(signOneway1, signOneway2);

    // 6. Fahrradstraße (Tempo 30, salmon asphalt, 5.5m wide, east-west split at roundabout and cross streets)
    this.createFahrradstrasse('fahrrad_ew_1', new THREE.Vector3(-116, 0, 30), new THREE.Vector3(-53.25, 0, 30));
    this.createFahrradstrasse('fahrrad_ew_2', new THREE.Vector3(-46.75, 0, 30), new THREE.Vector3(-22, 0, 30));
    this.createFahrradstrasse('fahrrad_we_1', new THREE.Vector3(22, 0, 30), new THREE.Vector3(46.75, 0, 30));
    this.createFahrradstrasse('fahrrad_we_2', new THREE.Vector3(53.25, 0, 30), new THREE.Vector3(116, 0, 30));

    // Signs for Fahrradstraße (Z244, Z245)
    const signFahrradStart = TrafficSigns.createSign('Z244_FAHRRADSTRASSE', new THREE.Vector3(-114, 0, 33.5), Math.PI / 2);
    const signFahrradEnd = TrafficSigns.createSign('Z245_ENDE_FAHRRADSTRASSE', new THREE.Vector3(114, 0, 33.5), -Math.PI / 2);
    this.group.add(signFahrradStart, signFahrradEnd);
  }

  public createFahrradstrasse(id: string, start: THREE.Vector3, end: THREE.Vector3) {
    const width = 5.5;
    const length = start.distanceTo(end);
    const midPoint = start.clone().add(end).multiplyScalar(0.5);
    const dir = end.clone().sub(start).normalize();
    const angleY = Math.atan2(dir.x, dir.z);

    const roadGroup = new THREE.Group();
    roadGroup.position.copy(midPoint);
    roadGroup.rotation.y = angleY;

    // Salmon-tinted bicycle road asphalt
    const fahrradMat = new THREE.MeshStandardMaterial({
      color: 0xb07060,
      roughness: 0.88,
      metalness: 0.05,
    });
    const asphaltGeom = new THREE.PlaneGeometry(width, length);
    const asphalt = new THREE.Mesh(asphaltGeom, fahrradMat);
    asphalt.rotation.x = -Math.PI / 2;
    asphalt.position.y = 0.005;
    asphalt.receiveShadow = true;
    roadGroup.add(asphalt);

    // Stone curbs
    const curbWidth = 0.20;
    const curbGeom = new THREE.BoxGeometry(curbWidth, 0.14, length);
    const curbL = new THREE.Mesh(curbGeom, this.curbMat);
    curbL.position.set(-width / 2 - curbWidth / 2, 0.07, 0);
    curbL.castShadow = true;
    const curbR = new THREE.Mesh(curbGeom, this.curbMat);
    curbR.position.set(width / 2 + curbWidth / 2, 0.07, 0);
    curbR.castShadow = true;
    roadGroup.add(curbL, curbR);

    // Sidewalks
    const swWidth = 2.4;
    const swGeom = new THREE.BoxGeometry(swWidth, 0.10, length);
    const swL = new THREE.Mesh(swGeom, this.sidewalkMat);
    swL.position.set(-width / 2 - curbWidth - swWidth / 2, 0.05, 0);
    const swR = new THREE.Mesh(swGeom, this.sidewalkMat);
    swR.position.set(width / 2 + curbWidth + swWidth / 2, 0.05, 0);
    roadGroup.add(swL, swR);

    // White bicycle stencils along road center every 30m
    const numStencils = Math.floor(length / 30.0);
    for (let i = 0; i < numStencils; i++) {
      const zPos = -length / 2 + 15 + i * 30.0;
      const stencil = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 2.4), this.lineWhiteMat);
      stencil.rotation.x = -Math.PI / 2;
      stencil.position.set(0, 0.012, zPos);
      roadGroup.add(stencil);
    }

    this.group.add(roadGroup);

    this.segments.push({
      id,
      start,
      end,
      width,
      speedLimitKmh: 30,
      hasSolidLine: false,
      isPriorityRoad: false,
      isBicycleStreet: true,
      isOneWay: false,
      isMotorway: false,
    });
  }

  public createZebraCrossing(pos: THREE.Vector3, roadWidth: number, rotationY: number) {
    const zebraGroup = new THREE.Group();
    zebraGroup.position.copy(pos);
    zebraGroup.rotation.y = rotationY;

    // 50cm wide white stripes with 50cm gaps
    const stripeWidth = 0.50;
    const stripeLength = 3.2;
    const numStripes = Math.floor(roadWidth / 1.0);
    const stripeGeom = new THREE.PlaneGeometry(stripeWidth, stripeLength);

    for (let i = 0; i < numStripes; i++) {
      const stripe = new THREE.Mesh(stripeGeom, this.lineWhiteMat);
      stripe.rotation.x = -Math.PI / 2;
      const xOffset = -roadWidth / 2 + 0.5 + i * 1.0;
      stripe.position.set(xOffset, 0.014, 0);
      zebraGroup.add(stripe);
    }

    this.group.add(zebraGroup);
  }

  public createLaneArrow(type: 'straight' | 'left' | 'right' | 'straight-left' | 'straight-right', pos: THREE.Vector3, rotationY: number) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 128, 256);
    ctx.fillStyle = '#ffffff';

    if (type === 'straight') {
      // Straight arrow
      ctx.beginPath();
      ctx.moveTo(64, 20);
      ctx.lineTo(96, 70);
      ctx.lineTo(76, 70);
      ctx.lineTo(76, 230);
      ctx.lineTo(52, 230);
      ctx.lineTo(52, 70);
      ctx.lineTo(32, 70);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'left') {
      // Left turn arrow
      ctx.beginPath();
      ctx.moveTo(20, 50);
      ctx.lineTo(60, 20);
      ctx.lineTo(60, 40);
      ctx.quadraticCurveTo(80, 50, 80, 120);
      ctx.lineTo(80, 230);
      ctx.lineTo(56, 230);
      ctx.lineTo(56, 120);
      ctx.quadraticCurveTo(56, 70, 40, 60);
      ctx.lineTo(60, 60);
      ctx.lineTo(20, 50);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'right') {
      // Right turn arrow
      ctx.beginPath();
      ctx.moveTo(108, 50);
      ctx.lineTo(68, 20);
      ctx.lineTo(68, 40);
      ctx.quadraticCurveTo(48, 50, 48, 120);
      ctx.lineTo(48, 230);
      ctx.lineTo(72, 230);
      ctx.lineTo(72, 120);
      ctx.quadraticCurveTo(72, 70, 88, 60);
      ctx.lineTo(68, 60);
      ctx.lineTo(108, 50);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'straight-left') {
      // Combined Straight and Left arrow (Zeichen 297)
      // Straight stem and head
      ctx.beginPath();
      ctx.moveTo(80, 20);
      ctx.lineTo(108, 65);
      ctx.lineTo(92, 65);
      ctx.lineTo(92, 230);
      ctx.lineTo(72, 230);
      ctx.lineTo(72, 65);
      ctx.lineTo(54, 65);
      ctx.closePath();
      ctx.fill();

      // Left branch
      ctx.beginPath();
      ctx.moveTo(14, 60);
      ctx.lineTo(50, 30);
      ctx.lineTo(50, 50);
      ctx.quadraticCurveTo(72, 60, 72, 120);
      ctx.lineTo(72, 170);
      ctx.lineTo(54, 170);
      ctx.lineTo(54, 120);
      ctx.quadraticCurveTo(54, 80, 34, 70);
      ctx.lineTo(50, 70);
      ctx.lineTo(14, 60);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'straight-right') {
      // Combined Straight and Right arrow (Zeichen 297)
      // Straight stem and head
      ctx.beginPath();
      ctx.moveTo(48, 20);
      ctx.lineTo(76, 65);
      ctx.lineTo(58, 65);
      ctx.lineTo(58, 230);
      ctx.lineTo(38, 230);
      ctx.lineTo(38, 65);
      ctx.lineTo(20, 65);
      ctx.closePath();
      ctx.fill();

      // Right branch
      ctx.beginPath();
      ctx.moveTo(114, 60);
      ctx.lineTo(78, 30);
      ctx.lineTo(78, 50);
      ctx.quadraticCurveTo(56, 60, 56, 120);
      ctx.lineTo(56, 170);
      ctx.lineTo(74, 170);
      ctx.lineTo(74, 120);
      ctx.quadraticCurveTo(74, 80, 94, 70);
      ctx.lineTo(78, 70);
      ctx.lineTo(114, 60);
      ctx.closePath();
      ctx.fill();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    const arrowMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.95 });
    const arrowMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 4.0), arrowMat);
    arrowMesh.rotation.x = -Math.PI / 2;
    arrowMesh.rotation.z = rotationY;
    arrowMesh.position.set(pos.x, 0.016, pos.z);

    this.group.add(arrowMesh);
  }

  public createSpeedLimitMarking(speed: 30 | 50, pos: THREE.Vector3, rotationY: number) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 256, 256);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 14;

    // White ellipse
    ctx.beginPath();
    ctx.ellipse(128, 128, 100, 115, 0, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 110px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${speed}`, 128, 130);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    const speedMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.92 });
    const speedMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 3.2), speedMat);
    speedMesh.rotation.x = -Math.PI / 2;
    speedMesh.rotation.z = rotationY;
    speedMesh.position.set(pos.x, 0.016, pos.z);

    this.group.add(speedMesh);
  }

  public createWartelinie(pos: THREE.Vector3, laneWidth: number, rotationY: number) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;


    ctx.clearRect(0, 0, 256, 64);
    ctx.fillStyle = '#ffffff';

    // German Zeichen 341 sharks teeth triangles (Haifischzähne)
    const teethCount = 6;
    const toothWidth = 256 / teethCount;
    for (let i = 0; i < teethCount; i++) {
      ctx.beginPath();
      ctx.moveTo(i * toothWidth + 4, 58);
      ctx.lineTo((i + 0.5) * toothWidth, 6);
      ctx.lineTo((i + 1) * toothWidth - 4, 58);
      ctx.closePath();
      ctx.fill();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    const teethMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
    const teethMesh = new THREE.Mesh(new THREE.PlaneGeometry(laneWidth, 0.8), teethMat);
    teethMesh.rotation.x = -Math.PI / 2;
    teethMesh.rotation.z = rotationY;
    teethMesh.position.set(pos.x, 0.016, pos.z);

    this.group.add(teethMesh);
  }

  public createSperrflaeche(pos: THREE.Vector3, width: number, length: number, rotationY: number) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, 128, 256);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 10;

    // Solid border per StVO Zeichen 298
    ctx.strokeRect(6, 6, 116, 244);

    // Diagonal chevron hatches at 45°
    for (let y = -50; y < 300; y += 36) {
      ctx.beginPath();
      ctx.moveTo(10, y);
      ctx.lineTo(118, y + 48);
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    const sperrMat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
    });
    const sperrMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, length), sperrMat);
    sperrMesh.rotation.x = -Math.PI / 2;
    sperrMesh.rotation.z = rotationY;
    sperrMesh.position.set(pos.x, 0.015, pos.z);
    sperrMesh.name = 'sperrflaeche';
    sperrMesh.userData = { isSperrflaeche: true };

    this.sperrGroup.add(sperrMesh);
  }

  public createCornerSperrflaeche(
    pos: THREE.Vector3,
    width: number,
    length: number,
    rotationY: number,
    corner: 'left' | 'right' = 'right'
  ) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, 128, 256);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 8;

    // German StVO Zeichen 298: Solid outer border for triangular corner taper
    ctx.beginPath();
    if (corner === 'right') {
      ctx.moveTo(8, 8);
      ctx.lineTo(120, 248);
      ctx.lineTo(8, 248);
    } else {
      ctx.moveTo(120, 8);
      ctx.lineTo(120, 248);
      ctx.lineTo(8, 248);
    }
    ctx.closePath();
    ctx.stroke();

    // 45° diagonal white chevron hatching inside clipped triangle
    ctx.save();
    ctx.clip();
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#ffffff';
    for (let y = -50; y < 350; y += 32) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(128, y + 48);
      ctx.stroke();
    }
    ctx.restore();

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, length), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = rotationY;
    mesh.position.set(pos.x, 0.015, pos.z);
    mesh.name = `sperrflaeche_corner_${corner}`;
    mesh.userData = { isSperrflaeche: true };
    this.sperrGroup.add(mesh);
  }

  public createAsphaltStencil(
    text: string,
    pos: THREE.Vector3,
    rotationY: number,
    width: number = 2.2,
    length: number = 3.6
  ) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, 512, 256);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 100px "Arial Black", "Impact", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 128);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    const stencilMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.92 });
    const stencilMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, length), stencilMat);
    stencilMesh.rotation.x = -Math.PI / 2;
    stencilMesh.rotation.z = rotationY;
    stencilMesh.position.set(pos.x, 0.016, pos.z);

    this.group.add(stencilMesh);
  }

  public createZickZackLinie(pos: THREE.Vector3, length: number, rotationY: number) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 128, 512);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const periods = Math.max(2, Math.floor(length / 2.5));
    const stepY = 500 / periods;
    ctx.beginPath();
    ctx.moveTo(20, 8);
    for (let i = 0; i < periods; i++) {
      ctx.lineTo(108, 8 + i * stepY + stepY * 0.5);
      ctx.lineTo(20, 8 + (i + 1) * stepY);
    }
    ctx.stroke();

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.92 });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1.2, length), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = rotationY;
    mesh.position.set(pos.x, 0.016, pos.z);
    this.group.add(mesh);
  }

  public createRailwayCrossing(pos: THREE.Vector3, roadWidth: number = 8.0) {
    const rxGroup = new THREE.Group();
    rxGroup.position.copy(pos);

    // 1. Gravel ballast bed (Schotterbett)
    const ballastMat = new THREE.MeshStandardMaterial({ color: 0x57534e, roughness: 0.95 });
    const ballast = new THREE.Mesh(new THREE.PlaneGeometry(4.0, 24.0), ballastMat);
    ballast.rotation.x = -Math.PI / 2;
    ballast.position.y = 0.007;
    ballast.receiveShadow = true;
    rxGroup.add(ballast);

    // 2. Wooden Sleepers (Holzschwellen)
    const sleeperMat = new THREE.MeshStandardMaterial({ color: 0x3e2723, roughness: 0.9 });
    const sleeperGeom = new THREE.BoxGeometry(2.4, 0.04, 0.22);
    for (let z = -10; z <= 10; z += 0.65) {
      const sleeper = new THREE.Mesh(sleeperGeom, sleeperMat);
      sleeper.position.set(0, 0.015, z);
      rxGroup.add(sleeper);
    }

    // 3. Steel Rails (Vignolschienen) - standard gauge
    const railMat = new THREE.MeshStandardMaterial({ color: 0xd4d4d8, metalness: 0.9, roughness: 0.2 });
    const railGeom = new THREE.BoxGeometry(0.08, 0.06, 24.0);
    const railL = new THREE.Mesh(railGeom, railMat);
    railL.position.set(-0.72, 0.035, 0);
    const railR = new THREE.Mesh(railGeom, railMat);
    railR.position.set(0.72, 0.035, 0);
    rxGroup.add(railL, railR);

    // 4. StVO Haltelinie (Zeichen 294) 3.0m before rails on approach lanes
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const laneW = roadWidth / 2; // 4.0m
    // Eastbound approach (from West, x < 0 relative): Haltelinie at x = -3.0
    const stopWest = new THREE.Mesh(new THREE.PlaneGeometry(0.50, laneW), lineMat);
    stopWest.rotation.x = -Math.PI / 2;
    stopWest.position.set(-3.0, 0.016, laneW / 2);

    // Westbound approach (from East, x > 0 relative): Haltelinie at x = +3.0
    const stopEast = new THREE.Mesh(new THREE.PlaneGeometry(0.50, laneW), lineMat);
    stopEast.rotation.x = -Math.PI / 2;
    stopEast.position.set(3.0, 0.016, -laneW / 2);

    rxGroup.add(stopWest, stopEast);

    // 5. Andreaskreuz Signposts (Zeichen 201) on curbs facing oncoming traffic
    // Eastbound approach: on right curb (z > 0), faces West (-Math.PI / 2)
    const akWest = TrafficSigns.createAndreaskreuzPost(new THREE.Vector3(-3.2, 0, roadWidth / 2 + 1.2), -Math.PI / 2);
    // Westbound approach: on right curb (z < 0), faces East (Math.PI / 2)
    const akEast = TrafficSigns.createAndreaskreuzPost(new THREE.Vector3(3.2, 0, -roadWidth / 2 - 1.2), Math.PI / 2);
    rxGroup.add(akWest, akEast);

    if (this.physicsWorld) {
      const akWestBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.dynamicPropMaterial,
        collisionFilterGroup: COLLISION_GROUP_DYNAMIC_PROP,
        collisionFilterMask:
          COLLISION_GROUP_GROUND |
          COLLISION_GROUP_CHASSIS |
          COLLISION_GROUP_AI_VEHICLE |
          COLLISION_GROUP_STATIC,
      });
      akWestBody.addShape(new CANNON.Cylinder(0.08, 0.08, 3.0, 8), new CANNON.Vec3(0, 1.5, 0));
      akWestBody.position.set(pos.x - 3.2, 0, pos.z + roadWidth / 2 + 1.2);
      this.physicsWorld.world.addBody(akWestBody);

      const akEastBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.dynamicPropMaterial,
        collisionFilterGroup: COLLISION_GROUP_DYNAMIC_PROP,
        collisionFilterMask:
          COLLISION_GROUP_GROUND |
          COLLISION_GROUP_CHASSIS |
          COLLISION_GROUP_AI_VEHICLE |
          COLLISION_GROUP_STATIC,
      });
      akEastBody.addShape(new CANNON.Cylinder(0.08, 0.08, 3.0, 8), new CANNON.Vec3(0, 1.5, 0));
      akEastBody.position.set(pos.x + 3.2, 0, pos.z - roadWidth / 2 - 1.2);
      this.physicsWorld.world.addBody(akEastBody);
    }

    this.group.add(rxGroup);
  }

  public createStraightRoad(
    id: string,
    start: THREE.Vector3,
    end: THREE.Vector3,
    width: number,
    speedLimitKmh: number,
    centerLine: 'solid' | 'dashed' | 'none' | boolean,
    isPriorityRoad: boolean,
    hasEdgeLines: boolean = true,
    isMotorway: boolean = false,
    isOneWay: boolean = false,
    isBicycleStreet: boolean = false,
    curbSides: 'both' | 'left' | 'right' | 'none' = 'both',
    swSides: 'both' | 'left' | 'right' | 'none' = 'both'
  ) {
    const length = start.distanceTo(end);
    const midPoint = start.clone().add(end).multiplyScalar(0.5);
    const dir = end.clone().sub(start).normalize();
    const angleY = Math.atan2(dir.x, dir.z);

    const roadGroup = new THREE.Group();
    roadGroup.position.copy(midPoint);
    roadGroup.rotation.y = angleY;

    // Asphalt surface
    const asphaltGeom = new THREE.PlaneGeometry(width, length);
    const asphalt = new THREE.Mesh(asphaltGeom, this.roadMaterial);
    asphalt.rotation.x = -Math.PI / 2;
    asphalt.position.y = 0.005;
    asphalt.receiveShadow = true;
    roadGroup.add(asphalt);

    // 3D Raised Stone Curbs bordering asphalt edges (matching reference image)
    const curbWidth = 0.20;
    const curbGeom = new THREE.BoxGeometry(curbWidth, 0.14, length);
    if (curbSides === 'both' || curbSides === 'left') {
      const curbL = new THREE.Mesh(curbGeom, this.curbMat);
      curbL.position.set(-width / 2 - curbWidth / 2, 0.07, 0);
      curbL.castShadow = true;
      curbL.receiveShadow = true;
      roadGroup.add(curbL);
    }

    if (curbSides === 'both' || curbSides === 'right') {
      const curbR = new THREE.Mesh(curbGeom, this.curbMat);
      curbR.position.set(width / 2 + curbWidth / 2, 0.07, 0);
      curbR.castShadow = true;
      curbR.receiveShadow = true;
      roadGroup.add(curbR);
    }

    // Sidewalks on both sides (adjacent to curbs)
    const swWidth = 2.4;
    const swGeom = new THREE.BoxGeometry(swWidth, 0.10, length);

    if (swSides === 'both' || swSides === 'left') {
      const swL = new THREE.Mesh(swGeom, this.sidewalkMat);
      swL.position.set(-width / 2 - curbWidth - swWidth / 2, 0.05, 0);
      swL.receiveShadow = true;
      roadGroup.add(swL);
    }

    if (swSides === 'both' || swSides === 'right') {
      const swR = new THREE.Mesh(swGeom, this.sidewalkMat);
      swR.position.set(width / 2 + curbWidth + swWidth / 2, 0.05, 0);
      swR.receiveShadow = true;
      roadGroup.add(swR);
    }

    // In 30-zones: no center lines and no edge lines per VwV-StVO zu § 45 Abs. 1c
    const effectiveCenterLine = (speedLimitKmh === 30 || centerLine === 'none')
      ? 'none'
      : (centerLine === true || centerLine === 'solid') ? 'solid' : 'dashed';
    const effectiveEdgeLines = speedLimitKmh === 30 ? false : hasEdgeLines;

    // Center Markings (German DIN 1451)
    if (effectiveCenterLine === 'solid') {
      // Solid continuous line (Zeichen 295 - Width 12cm)
      const line = new THREE.Mesh(new THREE.PlaneGeometry(0.12, length), this.lineWhiteMat);
      line.rotation.x = -Math.PI / 2;
      line.position.y = 0.012;
      roadGroup.add(line);
    } else if (effectiveCenterLine === 'dashed') {
      // Dashed Leitlinie (Zeichen 340 - 3m dash, 6m gap)
      const numDashes = Math.floor(length / 9.0);
      const dashGeom = new THREE.PlaneGeometry(0.12, 3.0);
      for (let i = 0; i < numDashes; i++) {
        const dash = new THREE.Mesh(dashGeom, this.lineWhiteMat);
        dash.rotation.x = -Math.PI / 2;
        dash.position.set(0, 0.012, -length / 2 + 4.5 + i * 9.0);
        roadGroup.add(dash);
      }
    }

    // Outer lane edge markings (White line 12cm)
    if (effectiveEdgeLines) {
      const edgeL = new THREE.Mesh(new THREE.PlaneGeometry(0.12, length), this.lineWhiteMat);
      edgeL.rotation.x = -Math.PI / 2;
      edgeL.position.set(-width / 2 + 0.1, 0.012, 0);

      const edgeR = new THREE.Mesh(new THREE.PlaneGeometry(0.12, length), this.lineWhiteMat);
      edgeR.rotation.x = -Math.PI / 2;
      edgeR.position.set(width / 2 - 0.1, 0.012, 0);

      roadGroup.add(edgeL, edgeR);
    }

    // For 4-lane boulevards (width >= 14m), add dashed lane division lines at ±3.75m from center
    if (width >= 14.0) {
      const numDashes = Math.floor(length / 9.0);
      const dashGeom = new THREE.PlaneGeometry(0.12, 3.0);
      for (let i = 0; i < numDashes; i++) {
        const dashL = new THREE.Mesh(dashGeom, this.lineWhiteMat);
        dashL.rotation.x = -Math.PI / 2;
        dashL.position.set(-3.75, 0.012, -length / 2 + 4.5 + i * 9.0);

        const dashR = new THREE.Mesh(dashGeom, this.lineWhiteMat);
        dashR.rotation.x = -Math.PI / 2;
        dashR.position.set(3.75, 0.012, -length / 2 + 4.5 + i * 9.0);

        roadGroup.add(dashL, dashR);
      }
    }

    this.group.add(roadGroup);

    this.segments.push({
      id,
      start,
      end,
      width,
      speedLimitKmh,
      hasSolidLine: effectiveCenterLine === 'solid',
      isPriorityRoad,
      isMotorway,
      isOneWay,
      isBicycleStreet,
    });
  }

  private buildIntersection(pos: THREE.Vector3, size: number, withLights: boolean) {
    const interGroup = new THREE.Group();
    interGroup.position.copy(pos);

    // Center asphalt square
    const asphalt = new THREE.Mesh(new THREE.PlaneGeometry(size, size), this.roadMaterial);
    asphalt.rotation.x = -Math.PI / 2;
    asphalt.position.y = 0.006;
    asphalt.receiveShadow = true;
    interGroup.add(asphalt);

    // Stop lines (Haltelinie Zeichen 294) - ONLY for signalized intersections!
    // Must span strictly the right-hand approach lane (not the opposing lane).
    if (withLights) {
      const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const laneWidth = size / 2; // 4.0m
      const stopLineThickness = 0.50; // 50cm StVO Haltlinie

      // South approach (driver moving North on x in [-laneWidth, 0]):
      const stopSouth = new THREE.Mesh(new THREE.PlaneGeometry(laneWidth, stopLineThickness), lineMat);
      stopSouth.rotation.x = -Math.PI / 2;
      stopSouth.position.set(-laneWidth / 2, 0.015, -size / 2 - 0.8);

      // North approach (driver moving South on x in [0, laneWidth]):
      const stopNorth = new THREE.Mesh(new THREE.PlaneGeometry(laneWidth, stopLineThickness), lineMat);
      stopNorth.rotation.x = -Math.PI / 2;
      stopNorth.position.set(laneWidth / 2, 0.015, size / 2 + 0.8);

      // East approach (driver moving West on z in [0, laneWidth]):
      const stopEast = new THREE.Mesh(new THREE.PlaneGeometry(laneWidth, stopLineThickness), lineMat);
      stopEast.rotation.x = -Math.PI / 2;
      stopEast.rotation.z = Math.PI / 2;
      stopEast.position.set(size / 2 + 0.8, 0.015, laneWidth / 2);

      // West approach (driver moving East on z in [-laneWidth, 0]):
      const stopWest = new THREE.Mesh(new THREE.PlaneGeometry(laneWidth, stopLineThickness), lineMat);
      stopWest.rotation.x = -Math.PI / 2;
      stopWest.rotation.z = Math.PI / 2;
      stopWest.position.set(-size / 2 - 0.8, 0.015, -laneWidth / 2);

      interGroup.add(stopSouth, stopNorth, stopEast, stopWest);
    }

    this.group.add(interGroup);
  }

  public createTurnTrajectoryArrows(centerPos: THREE.Vector3, type: 'left' | 'right' | 'both', rotationY: number = 0) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 256, 256);
    // Authentic German StVO white dashed transition lines (Zeichen 340 Leitlinie in Knotenpunkten)
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 8;
    ctx.setLineDash([16, 16]);
    ctx.lineCap = 'butt';

    if (type === 'left' || type === 'both') {
      ctx.beginPath();
      ctx.moveTo(128, 230);
      ctx.quadraticCurveTo(128, 120, 30, 80);
      ctx.stroke();
    }

    if (type === 'right' || type === 'both') {
      ctx.beginPath();
      ctx.moveTo(128, 230);
      ctx.quadraticCurveTo(128, 120, 226, 80);
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.85 });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(6.0, 6.0), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = rotationY;
    mesh.position.set(centerPos.x, 0.018, centerPos.z);
    this.group.add(mesh);
  }

  public getSpeedLimitAt(pos: THREE.Vector3): number {
    let bestDist = Infinity;
    let bestSegment: RoadSegment | null = null;

    for (const seg of this.segments) {
      const dx = seg.end.x - seg.start.x;
      const dz = seg.end.z - seg.start.z;
      const lenSq = dx * dx + dz * dz;
      if (lenSq === 0) continue;

      let t = ((pos.x - seg.start.x) * dx + (pos.z - seg.start.z) * dz) / lenSq;
      t = Math.max(0, Math.min(1, t));

      const projX = seg.start.x + t * dx;
      const projZ = seg.start.z + t * dz;

      const dist = Math.hypot(pos.x - projX, pos.z - projZ);
      const corridorWidth = seg.width / 2 + 2.0;

      if (dist <= corridorWidth) {
        // Priority road takes precedence inside intersection overlap zones
        if (
          !bestSegment ||
          (seg.isPriorityRoad && !bestSegment.isPriorityRoad) ||
          (!bestSegment.isPriorityRoad && dist < bestDist) ||
          (seg.isPriorityRoad === bestSegment.isPriorityRoad && dist < bestDist)
        ) {
          bestDist = dist;
          bestSegment = seg;
        }
      }
    }

    return bestSegment ? bestSegment.speedLimitKmh : 50;
  }
}
